/**
 * Local Storage Utilities
 * Safe access to localStorage with proper error handling
 */

interface SafeWindow {
  localStorage: {
    getItem: (key: string) => string | null;
    setItem: (key: string, value: string) => void;
    removeItem: (key: string) => void;
  };
}

// Type guard to check if localStorage is available
const isLocalStorageAvailable = (): boolean => {
  try {
    return typeof window !== 'undefined' && 'localStorage' in window;
  } catch (error) {
    console.warn('localStorage is not available:', error);
    return false;
  }
};

export const getStorageItem = (key: string): string | null => {
  if (!isLocalStorageAvailable()) return null;
  
  try {
    return (window as SafeWindow).localStorage.getItem(key);
  } catch (error) {
    console.warn(`Failed to read from localStorage (key: ${key}):`, error);
    return null;
  }
};

export const setStorageItem = (key: string, value: string): boolean => {
  if (!isLocalStorageAvailable()) return false;
  
  try {
    (window as SafeWindow).localStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.warn(`Failed to write to localStorage (key: ${key}):`, error);
    return false;
  }
};

export const removeStorageItem = (key: string): boolean => {
  if (!isLocalStorageAvailable()) return false;
  
  try {
    (window as SafeWindow).localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.warn(`Failed to remove from localStorage (key: ${key}):`, error);
    return false;
  }
};
